/**
 * Filter panel component
 * Clean horizontal filter bar for Results page
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Input,
  Select,
  Slider,
  Button,
  Drawer,
  Form,
  Tag,
  Badge,
  Checkbox,
  Space,
} from 'antd';
import { DEBOUNCE } from '@/utils/constants';
import {
  FilterOutlined,
  ClearOutlined,
  StarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { PlaceFilter, SearchTermOption } from '@/types/place';
import type { CountryOption } from '@/services/placesApi';

interface FilterPanelProps {
  filters: PlaceFilter;
  onChange: (filters: PlaceFilter) => void;
  onClear: () => void;
  categories?: string[];
  countries?: CountryOption[];
  searchTerms?: SearchTermOption[];
  loading?: boolean;
}

export function FilterPanel({
  filters,
  onChange,
  onClear,
  categories = [],
  countries = [],
  searchTerms = [],
  loading,
}: FilterPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Local state for search input to enable debouncing
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Ref to always have the latest filters - prevents stale closure in debounced search
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Sync local search value when filters change externally (e.g., clear)
  // Also cancel any pending debounce to prevent stale searches from firing
  useEffect(() => {
    setSearchValue(filters.search || '');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, [filters.search]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Count active filters
  const categoryCount = Array.isArray(filters.category) ? filters.category.length : (filters.category ? 1 : 0);
  const countryCount = Array.isArray(filters.country) ? filters.country.length : (filters.country ? 1 : 0);
  const searchTermCount = Array.isArray(filters.searchTerm) ? filters.searchTerm.length : (filters.searchTerm ? 1 : 0);
  const activeFilterCount = [
    filters.search,
    categoryCount > 0,
    countryCount > 0,
    searchTermCount > 0,
    filters.minRating,
    filters.hasPhone !== undefined,
    filters.hasWebsite !== undefined,
    filters.hasAddress !== undefined,
    filters.hasRating !== undefined,
    filters.hasCoordinates !== undefined,
    filters.hasCategory !== undefined,
    filters.hasPlusCode !== undefined,
    filters.hasOpeningHours !== undefined,
    filters.priceLevel?.length,
  ].filter(Boolean).length;

  const handleSearchTermChange = (value: string[] | undefined) => {
    onChange({ ...filters, searchTerm: value && value.length > 0 ? value : undefined });
  };

  // Debounced search to prevent excessive API calls
  // Uses filtersRef to always read the latest filters, avoiding stale closure
  const triggerSearch = useCallback((value: string) => {
    onChange({ ...filtersRef.current, search: value || undefined });
  }, [onChange]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value); // Update input immediately for responsiveness

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new debounced timeout
    searchTimeoutRef.current = setTimeout(() => {
      triggerSearch(value);
    }, DEBOUNCE.SEARCH);
  };

  // Immediate search (for Enter key)
  const handleSearchNow = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    triggerSearch(searchValue);
  };

  const handleCategoryChange = (value: string[] | undefined) => {
    onChange({ ...filters, category: value && value.length > 0 ? value : undefined });
  };

  const handleCountryChange = (value: string[] | undefined) => {
    onChange({ ...filters, country: value && value.length > 0 ? value : undefined });
  };

  const handleRatingChange = (value: number | undefined) => {
    onChange({ ...filters, minRating: value });
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Filter controls row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="Search places..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onSearch={handleSearchNow}
            onPressEnter={handleSearchNow}
            style={{ width: 220 }}
            allowClear
            loading={loading}
            enterButton
          />

          {countries.length > 0 && (
            <Select
              mode="multiple"
              placeholder="Country"
              value={Array.isArray(filters.country) ? filters.country : (filters.country ? [filters.country] : [])}
              onChange={handleCountryChange}
              options={countries.map((c) => ({
                value: c.country,
                label: `${c.country} (${c.count.toLocaleString()})`,
              }))}
              style={{ width: 200 }}
              allowClear
              showSearch
              maxTagCount={1}
              optionFilterProp="label"
            />
          )}

          <Select
            mode="multiple"
            placeholder="Category"
            value={Array.isArray(filters.category) ? filters.category : (filters.category ? [filters.category] : [])}
            onChange={handleCategoryChange}
            options={categories.map((cat) => ({ value: cat, label: cat }))}
            style={{ width: 200 }}
            allowClear
            showSearch
            maxTagCount={1}
          />

          {searchTerms.length > 0 && (
            <Select
              mode="multiple"
              placeholder="Search term"
              value={Array.isArray(filters.searchTerm) ? filters.searchTerm : (filters.searchTerm ? [filters.searchTerm] : [])}
              onChange={handleSearchTermChange}
              options={searchTerms.map((term) => ({
                value: term.searchQuery,
                label: term.remainingCount !== undefined && term.remainingCount !== term.placeCount
                  ? `${term.searchQuery} (${term.remainingCount} / ${term.placeCount})`
                  : `${term.searchQuery} (${term.placeCount})`,
              }))}
              style={{ width: 220 }}
              allowClear
              showSearch
              maxTagCount={1}
              optionFilterProp="label"
              suffixIcon={<SearchOutlined />}
            />
          )}

          <Badge count={activeFilterCount} size="small">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setDrawerOpen(true)}
            >
              More
            </Button>
          </Badge>

          {activeFilterCount > 0 && (
            <Button type="text" size="small" icon={<ClearOutlined />} onClick={onClear}>
              Clear
            </Button>
          )}
        </div>

        {/* Active filter tags */}
        {activeFilterCount > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {filters.search && (
              <Tag closable onClose={() => {
                setSearchValue('');
                onChange({ ...filters, search: undefined });
              }}>
                Search: {filters.search}
              </Tag>
            )}
            {filters.category && (Array.isArray(filters.category) ? filters.category : [filters.category]).map((cat) => (
              <Tag
                key={cat}
                closable
                onClose={() => {
                  const current = Array.isArray(filters.category) ? filters.category : [filters.category];
                  const newCategories = current.filter((c) => c !== cat);
                  handleCategoryChange(newCategories.length > 0 ? newCategories : undefined);
                }}
              >
                {cat}
              </Tag>
            ))}
            {filters.country && (Array.isArray(filters.country) ? filters.country : [filters.country]).map((c) => (
              <Tag
                key={c}
                closable
                onClose={() => {
                  const current = Array.isArray(filters.country) ? filters.country : [filters.country];
                  const newCountries = current.filter((x) => x !== c);
                  handleCountryChange(newCountries.length > 0 ? newCountries : undefined);
                }}
              >
                {c}
              </Tag>
            ))}
            {filters.searchTerm && (Array.isArray(filters.searchTerm) ? filters.searchTerm : [filters.searchTerm]).map((term) => (
              <Tag
                key={term}
                closable
                onClose={() => {
                  const current = Array.isArray(filters.searchTerm) ? filters.searchTerm : [filters.searchTerm];
                  const newTerms = current.filter((t) => t !== term);
                  handleSearchTermChange(newTerms.length > 0 ? newTerms : undefined);
                }}
              >
                {term}
              </Tag>
            ))}
            {filters.minRating && (
              <Tag closable onClose={() => handleRatingChange(undefined)}>
                Rating: {filters.minRating}+
              </Tag>
            )}
            {filters.priceLevel && filters.priceLevel.length > 0 && (
              <Tag
                closable
                onClose={() => onChange({ ...filters, priceLevel: undefined })}
              >
                Price: {'$'.repeat(Math.min(...filters.priceLevel))}-{'$'.repeat(Math.max(...filters.priceLevel))}
              </Tag>
            )}
            {filters.hasPhone !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasPhone: undefined })}>
                {filters.hasPhone ? 'Has phone' : 'No phone'}
              </Tag>
            )}
            {filters.hasWebsite !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasWebsite: undefined })}>
                {filters.hasWebsite ? 'Has website' : 'No website'}
              </Tag>
            )}
            {filters.hasAddress !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasAddress: undefined })}>
                {filters.hasAddress ? 'Has address' : 'No address'}
              </Tag>
            )}
            {filters.hasRating !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasRating: undefined })}>
                {filters.hasRating ? 'Has rating' : 'No rating'}
              </Tag>
            )}
            {filters.hasCoordinates !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasCoordinates: undefined })}>
                {filters.hasCoordinates ? 'Has coords' : 'No coords'}
              </Tag>
            )}
            {filters.hasCategory !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasCategory: undefined })}>
                {filters.hasCategory ? 'Has category' : 'No category'}
              </Tag>
            )}
            {filters.hasPlusCode !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasPlusCode: undefined })}>
                {filters.hasPlusCode ? 'Has Plus Code' : 'No Plus Code'}
              </Tag>
            )}
            {filters.hasOpeningHours !== undefined && (
              <Tag closable onClose={() => onChange({ ...filters, hasOpeningHours: undefined })}>
                {filters.hasOpeningHours ? 'Has hours' : 'No hours'}
              </Tag>
            )}
          </div>
        )}
      </div>

      {/* Advanced filters drawer */}
      <Drawer
        title="Advanced Filters"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={360}
        extra={
          <Button size="small" onClick={onClear}>
            Clear All
          </Button>
        }
      >
        <Form layout="vertical">
          {/* Category */}
          <Form.Item label="Category">
            <Select
              mode="multiple"
              placeholder="Select categories"
              value={Array.isArray(filters.category) ? filters.category : (filters.category ? [filters.category] : [])}
              onChange={handleCategoryChange}
              options={categories.map((cat) => ({ value: cat, label: cat }))}
              allowClear
              showSearch
              maxTagCount="responsive"
            />
          </Form.Item>

          {/* Rating range */}
          <Form.Item label="Minimum Rating">
            <Slider
              value={filters.minRating || 0}
              onChange={(value) => onChange({ ...filters, minRating: value || undefined })}
              min={0}
              max={5}
              step={0.5}
              marks={{
                0: '0',
                2.5: '2.5',
                5: '5',
              }}
              tooltip={{
                formatter: (value) => (
                  <span>
                    <StarOutlined /> {value}+
                  </span>
                ),
              }}
            />
          </Form.Item>

          {/* Price level */}
          <Form.Item label="Price Level">
            <Checkbox.Group
              value={filters.priceLevel}
              onChange={(values) =>
                onChange({
                  ...filters,
                  priceLevel: values.length > 0 ? (values as number[]) : undefined,
                })
              }
            >
              <Space direction="vertical">
                <Checkbox value={1}>$ - Budget</Checkbox>
                <Checkbox value={2}>$$ - Moderate</Checkbox>
                <Checkbox value={3}>$$$ - Expensive</Checkbox>
                <Checkbox value={4}>$$$$ - Very Expensive</Checkbox>
              </Space>
            </Checkbox.Group>
          </Form.Item>

          {/* Data presence filters */}
          <Form.Item label="Phone">
            <Select
              value={filters.hasPhone === undefined ? 'any' : filters.hasPhone ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasPhone: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has phone' },
                { value: 'missing', label: 'Missing phone' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Website">
            <Select
              value={filters.hasWebsite === undefined ? 'any' : filters.hasWebsite ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasWebsite: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has website' },
                { value: 'missing', label: 'Missing website' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Address">
            <Select
              value={filters.hasAddress === undefined ? 'any' : filters.hasAddress ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasAddress: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has address' },
                { value: 'missing', label: 'Missing address' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Rating">
            <Select
              value={filters.hasRating === undefined ? 'any' : filters.hasRating ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasRating: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has rating' },
                { value: 'missing', label: 'Missing rating' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Coordinates">
            <Select
              value={filters.hasCoordinates === undefined ? 'any' : filters.hasCoordinates ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasCoordinates: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has coordinates' },
                { value: 'missing', label: 'Missing coordinates' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Opening Hours">
            <Select
              value={filters.hasOpeningHours === undefined ? 'any' : filters.hasOpeningHours ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasOpeningHours: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has hours' },
                { value: 'missing', label: 'Missing hours' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Plus Code">
            <Select
              value={filters.hasPlusCode === undefined ? 'any' : filters.hasPlusCode ? 'has' : 'missing'}
              onChange={(value) =>
                onChange({
                  ...filters,
                  hasPlusCode: value === 'any' ? undefined : value === 'has',
                })
              }
              options={[
                { value: 'any', label: 'Any' },
                { value: 'has', label: 'Has Plus Code' },
                { value: 'missing', label: 'Missing Plus Code' },
              ]}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}

export default FilterPanel;
